import React, { useEffect, useState } from 'react';
import Panel from './Panel';
import { MouseButtons } from '../../interfaces/Input';

/**
 * use this when wanting to display each child component in its own tab, rendering only the currently selected one
 *
 * *note: child components can define their own 'tabheader' property to render a custom component as the label of their tab*
 * @export
 * @param {{
 * value: Number,
 * evenTabs: Boolean,
 * onTabChangedCallback: (newIndex: Number),
 * "data-name": String
 * }} props these special props may be used:
 * - value: the index of the tab content to render
 * - evenTabs: if true, tab headers will have equal sizes and fill the entire width of the root div container
 * - onTabChangedCallback: a callback function that gets executed whenever the current tab is changed, passing the index of the new current tab as the parameter
 * - "data-name": used for automation purposes to specifically identify this property. *defaults to "tabbed-display-root". optional*
 * @returns a formatted container with each child component placed in its own sub-container, paged into tabs
 */
export default function TabbedDisplay(props)
{
	const [currentTab, setCurrentTab] = useState(props.value ?? 0);

	useEffect(() =>
	{
		if (props.onTabChangedCallback)
		{
			props.onTabChangedCallback(currentTab);
		}
	}, [currentTab]);

	function generateTabs()
	{
		const childrenCount = React.Children.count(props.children);

		return React.Children.map(props.children, (child, idx) =>
		{
			const tabLabel = child.props["data-tabheader"] ?? child.props.tabheader ?? ("Tab " + idx)
			return (
				<div className={"tab"}
					data-name={"tab-" + tabLabel + "-" + tabLabel}
					data-status={idx === currentTab ? "active" : "inactive"}
					key={idx}
					style={
						!props.evenTabs ? {} :
							{
								width: (100 / childrenCount) + "%",
							}
					}
					onClick={(e) =>
					{
						if (e.button === MouseButtons.LEFT && currentTab !== idx)
						{
							setCurrentTab(idx);
						}
					}}
				>
					{tabLabel}
				</div>
			);
		});
	}

	return (
		<div className={"tabbedDisplayRoot"}
			data-name={props["data-name"] ?? "tabbed-display-root"}
		>
			<div className={"tabbedDisplayHeader"}
				data-name={props["data-name"] ? (props["data-name"] + "-header") : "tabbed-display-header"}
			>
				{
					generateTabs()
				}
			</div>
			<Panel className={"tabbedDisplayContentRoot solid bottom"}
				data-name={props["data-name"] ? (props["data-name"] + "-content-root") : "tabbed-display-content-root"}
			>
				{
					props.children[currentTab]
				}
			</Panel>
		</div>
	)
}